import PropTypes from 'prop-types';
import { Tooltip } from '@material-ui/core';
import { useState } from 'react';
import { useRecoilState } from 'recoil';

import { chatPostOnDemandAtom } from '@/state/atom/translationAtom';
import { trI18n } from '@/common/utils/trI18n';
import TranslationConstant from '@/common/constant/TranslationConstant';
import {
    requestChatPostOnDemandTranslation,
    setListChatPostOnDemandActiveView
} from '@/component/multiLanguage/handlers/ChatPostOnDemandHandler';

import './ChatPostOnDemandControl.css';
import DataChangePleaseReloadModal from '@/component/common/modal/DataChangePleaseReloadModal';

const { ON_DEMAND_TRANSLATION_STATUS, ON_DEMAND_TRANSLATION_VIEW } = TranslationConstant;

ChatPostOnDemandControl.propTypes = {
    threadId: PropTypes.string,
    ticketInfo: PropTypes.any,
    isOverlap: PropTypes.bool,
};

/**
 * 「翻訳版」toggle for a chat thread, rendered inside the header dropdown menu.
 *
 * Whole-thread toggle: ON translates every eligible message, OFF shows originals.
 * While translating, the switch is disabled and a loading indicator (一括操作 style)
 * is shown. Manages its own state via chatPostOnDemandAtom + handler functions,
 * matching the pattern used by DetailContentOnDemandControl.
 */
export default function ChatPostOnDemandControl({ threadId, ticketInfo, isOverlap = false }) {
    const [isOpenModalReloadPage, setIsOpenModalReloadPage] = useState(false);
    const [chatPostOnDemand, setChatPostOnDemand] = useRecoilState(chatPostOnDemandAtom);

    const threadRecord = threadId ? chatPostOnDemand[threadId] : null;
    const toggleStatus = threadRecord?.toggleStatus || ON_DEMAND_TRANSLATION_STATUS.IDLE;
    const activeView = threadRecord?.activeView || ON_DEMAND_TRANSLATION_VIEW.ORIGINAL;

    const isLoading = toggleStatus === ON_DEMAND_TRANSLATION_STATUS.LOADING;
    const isTranslated = toggleStatus === ON_DEMAND_TRANSLATION_STATUS.TRANSLATED;
    const isShowingTranslation = activeView === ON_DEMAND_TRANSLATION_VIEW.TRANSLATION;

    const handleToggleOnDemand = () => {
        if (isLoading) return;

        // Turning OFF -> show original (no re-translate), keep cached translations.
        // In 重ね表示 this applies to the whole stacked cluster (isOverlap).
        if (isShowingTranslation) {
            setListChatPostOnDemandActiveView(
                setChatPostOnDemand,
                threadId,
                ON_DEMAND_TRANSLATION_VIEW.ORIGINAL,
                isOverlap
            );
            return;
        }

        // Turning ON when a translation already exists in this session -> just show
        // it (do NOT call the API again). Applies to the whole cluster in 重ね表示.
        if (isTranslated) {
            setListChatPostOnDemandActiveView(
                setChatPostOnDemand,
                threadId,
                ON_DEMAND_TRANSLATION_VIEW.TRANSLATION,
                isOverlap
            );
            return;
        }

        // First ON (IDLE/ERROR): trigger the translation. On success the handler
        // switches activeView to TRANSLATION (toggle stays ON).
        requestChatPostOnDemandTranslation({
            threadId,
            ticketInfo,
            chatPostOnDemand,
            setChatPostOnDemand,
            isOverlap,
            setIsOpenModalReloadPage,
        });
    };

    const toggleId = `chat-ondemand-translation-toggle-${threadId}`;

    return (
        <div className="chat-ondemand-translation" data-cy="chat-ondemand-translation">
            <span className="chat-ondemand-translation-label">
                {trI18n('CM-ONDEMAND-TRANSLATION-TOGGLE-LABEL')}
            </span>

            <div style={{display: 'flex', alignItems: 'center', gap: 4}}>
                <div className="switch-check">
                    <input
                        name="chatOnDemandTranslation"
                        id={toggleId}
                        type="checkbox"
                        checked={isShowingTranslation}
                        disabled={isLoading}
                        onChange={handleToggleOnDemand}
                        data-cy="chat-ondemand-translation-toggle"
                    />
                    <label htmlFor={toggleId}></label>
                </div>

                {isLoading && (
                    <Tooltip title={trI18n('CM-ONDEMAND-TRANSLATION-LOADING-TOOLTIP')} arrow placement="right-end">
                        <img
                            className="option-icon icon-loading"
                            src={`/assets/image/icon-loading.svg`}
                            alt="icon-loading"
                            data-cy="chat-ondemand-translation-loading"
                        />
                    </Tooltip>
                )}
            </div>

            <DataChangePleaseReloadModal
                isOpen={isOpenModalReloadPage}
                setIsOpen={setIsOpenModalReloadPage}
            />
        </div>
    );
}
